const formatTime = (totalSeconds: number, extension?: string, leadingZero: boolean = true) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const ext = extension ? ` ${extension}` : '';

  const formattedMinutes = leadingZero ? String(minutes).padStart(2, '0') : String(minutes);

  return `${formattedMinutes}:${String(seconds).padStart(2, '0')}${ext}`;
};

export const formatSecondsToMinutes = (
  totalSeconds?: number,
  extension?: string,
  leadingZero?: boolean
): string | undefined => {
  if (!totalSeconds) return undefined;
  return formatTime(Math.floor(totalSeconds), extension, leadingZero);
};

export const formatMillisecondsToMinutes = (totalMilliseconds: number, extension?: string, leadingZero?: boolean) => {
  return formatTime(Math.floor(Math.floor(totalMilliseconds) / 1000), extension, leadingZero);
};

export const getPublishedLabel = (date: string): string => {
  const diff = (new Date().getTime() - new Date(date).getTime()) / 60000;
  const rtf = new Intl.RelativeTimeFormat('de', { style: 'short' });

  if (Math.abs(diff) < 60) return rtf.format(-Math.round(diff), 'minute');
  if (Math.abs(diff) < 1440) return rtf.format(-Math.round(diff / 60), 'hour');

  return new Date(date).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const getLocalOffsetString = (): string => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

export const formatIsoWithOffset = (input?: string, offsetHours?: number): string => {
  if (!input) return '';
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/.exec(input);
  if (!m) return input;
  const base = m[1];

  if (typeof offsetHours === 'number') {
    const sign = offsetHours >= 0 ? '+' : '-';
    const absHours = Math.abs(offsetHours);
    const hh = String(Math.floor(absHours)).padStart(2, '0');
    const mm = String(Math.round((absHours - Math.floor(absHours)) * 60)).padStart(2, '0');
    return `${base}${sign}${hh}:${mm}`;
  }

  return `${base}${getLocalOffsetString()}`;
};

export const getDateFormat = (datetime: string, withWeekday = false): string | undefined => {
  if (!datetime || datetime === '') {
    return undefined;
  }
  const d = new Date(datetime);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  const weekday = d.toLocaleDateString('de-DE', { weekday: 'short' }) + '., ';

  return (withWeekday ? weekday : '') + `${day}.${month}.${year}`;
};

export const formatDate = (datetime: string, withWeekday = false): string | undefined => {
  if (!datetime || datetime === '') {
    return undefined;
  }
  const d = new Date(datetime);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();

  const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const weekdayName = weekdays[d.getUTCDay()];
  const weekday = weekdayName + '., ';

  return (withWeekday ? weekday : '') + `${day}.${month}.${year}`;
};
